import { startCase, uniq, uniqWith } from 'es-toolkit';
import React from 'react';
import { SxProps, Theme } from '@mui/material';

import {
  INDETERMINATE,
  IndeterminateValue,
  MaterialPropertyValuePair,
  PropertySelectField,
} from '@paragon/client-tools';
import {
  formatLumberGrade,
  formatLumberSpeciesLong,
  Lumber,
  LumberGrade,
  LumberSpecies,
  LumberTreatmentType,
} from '@paragon/contract-library';
import { forceArray, naturalSort, naturalSortByProperty } from '@paragon/utilities';

import { useMaterialListsSelector } from '../lib/components/hooks';
import { lumbersAreEqual } from './lumberIdentity';
import { createNominalLumberDimensionFormatter, isLumberSuitableForNicknaming } from './lumberNicknameGeneratorFactory';
import {
  getClosestLumberToProposedLumber,
  getNominalDimensionString,
  gradeMatches,
  HasNominalDimensions,
  hasNominalDimensions,
  LumberPropertyLevel,
  nominalSizeMatches,
  speciesMatches,
} from './lumberSelectionUtility';

interface BaseProps<TLumber> {
  readonly lumbers: readonly Lumber[];
  readonly value: TLumber | TLumber[];
  readonly onChange: (patch: LumberPatch) => void;
  readonly inputSx?: SxProps<Theme>;
  readonly disabled?: boolean;
  readonly showAdvanced?: boolean;
}

interface StrictModeProps extends BaseProps<Lumber> {
  readonly strict: true;
  readonly alwaysValid?: boolean;
}

export interface LooseModeProps extends BaseProps<Partial<Lumber>> {
  readonly strict?: false;
  readonly placeholder: string;
}

export type LumberSelectProps = StrictModeProps | LooseModeProps;

export interface LumberPatch {
  patch: Partial<Lumber>;
  level: LumberPropertyLevel;
}

export function applyLumberPatch(lumber: Lumber, patch: LumberPatch, validLumbers: readonly Lumber[]): Lumber {
  return getClosestLumberToProposedLumber(validLumbers, { ...lumber, ...patch.patch });
}

export function applyLumberPatchToLumberLikeObject<T extends Partial<Lumber>>(obj: T, patch: LumberPatch): T {
  return { ...obj, ...patch.patch };
}

const placeholderSymbol = Symbol('Placeholder for undefined Lumber field');
type PlaceholderSymbol = typeof placeholderSymbol;

export function LumberSelects(props: LumberSelectProps) {
  const values = forceArray(props.value);

  const formatLumberDimensions = useMaterialListsSelector(createNominalLumberDimensionFormatter);

  if (
    props.strict &&
    props.alwaysValid &&
    !props.lumbers.some((lumber) => values.every((value) => lumbersAreEqual(lumber, value as Lumber)))
  ) {
    props.onChange({ patch: props.lumbers[0], level: LumberPropertyLevel.Thickness });
  }

  // Use a custom string for sorting because using formatLumberDimensions causes 4.375" to come before 4", because of
  // the quotations I believe.
  const dimensionOptions: Partial<Lumber>[] = naturalSortByProperty(
    uniqWith(props.lumbers, nominalSizeMatches),
    (lumber) => `${lumber.nominalThickness} ${lumber.nominalWidth}`,
  );

  let representativeLumbersForAvailableSpecies,
    representativeLumbersForAvailableGrades,
    representativeLumbersForAvailableTreatments;

  if (props.strict && (!Array.isArray(props.value) || props.value.length === 1)) {
    const value = Array.isArray(props.value) ? props.value[0] : props.value;

    // filter to species that work for the current dimensions
    representativeLumbersForAvailableSpecies = props.lumbers.filter((lumber) => nominalSizeMatches(value, lumber));

    // filter to grades that work for the current species
    representativeLumbersForAvailableGrades = representativeLumbersForAvailableSpecies.filter((lumber) =>
      speciesMatches(value, lumber),
    );

    // filter to treatments that work for the current grade
    representativeLumbersForAvailableTreatments = representativeLumbersForAvailableGrades.filter((lumber) =>
      gradeMatches(value, lumber),
    );
  } else {
    // no filtering
    representativeLumbersForAvailableSpecies = props.lumbers;
    representativeLumbersForAvailableGrades = props.lumbers;
    representativeLumbersForAvailableTreatments = props.lumbers;
  }

  const pickTreatment = (lumber: Lumber) => lumber.treatmentType;

  const speciesOptions: {
    species: LumberSpecies | PlaceholderSymbol;
    tangibleSpecies: readonly LumberSpecies[];
  }[] = uniqWith(
    naturalSortByProperty(
      representativeLumbersForAvailableSpecies.map((lumber) => ({
        species: lumber.species,
        tangibleSpecies: lumber.tangibleSpecies,
      })),
      ({ species, tangibleSpecies }) => formatLumberSpeciesLong(species, tangibleSpecies),
    ),
    speciesMatches,
  );
  const gradeOptions: { grade: LumberGrade | PlaceholderSymbol; tangibleGrades: readonly LumberGrade[] }[] = uniqWith(
    naturalSortByProperty(
      representativeLumbersForAvailableGrades.map((lumber) => ({
        grade: lumber.grade,
        tangibleGrades: lumber.tangibleGrades,
      })),
      ({ grade, tangibleGrades }) => formatLumberGrade(grade, tangibleGrades),
    ),
    gradeMatches,
  );
  const treatmentOptions: (LumberTreatmentType | PlaceholderSymbol)[] = uniq(
    representativeLumbersForAvailableTreatments.map(pickTreatment),
  ).toSorted(naturalSort);

  if (!props.strict) {
    // We need an entry in the select for `undefined`, so placeholderSymbol is used.
    speciesOptions.unshift({ species: placeholderSymbol, tangibleSpecies: [] });
    gradeOptions.unshift({ grade: placeholderSymbol, tangibleGrades: [] });
    treatmentOptions.unshift(placeholderSymbol);

    // An empty object will work to represent `undefined`
    //   due to the check for `hasNominalDimensions` in the dimension dropdown.
    dimensionOptions.unshift({});
  }

  // In non-strict mode, an empty object is used as the placeholder for an `undefined` dimension selection.  Thus, we
  //   can't always assume that a lumber will have nominal dimensions.
  const getLumberDimensionString = (lumber: Partial<HasNominalDimensions>) => {
    if (isLumberSuitableForNicknaming(lumber)) {
      return formatLumberDimensions(lumber);
    }

    if (hasNominalDimensions(lumber)) {
      return getNominalDimensionString(lumber);
    }

    return (!props.strict && props.placeholder ? props.placeholder : null) ?? '';
  };

  function renderSelectValue<T extends string | object>(value: T | PlaceholderSymbol) {
    return value === placeholderSymbol ? 'undefined' : `value-${value}`;
  }

  const dimensionsOfCurrentValues = uniqWith(
    values.filter((value) => value.nominalThickness != null && value.nominalWidth != null),
    nominalSizeMatches,
  ) as Pick<Lumber, 'nominalThickness' | 'nominalWidth'>[];
  const commonDimension: Lumber | Partial<Lumber> | IndeterminateValue | undefined =
    dimensionsOfCurrentValues.length === 1 ? dimensionsOfCurrentValues[0] : INDETERMINATE;

  const speciesOfCurrentValues = uniqWith(values, speciesMatches);
  const commonSpecies = (
    speciesOfCurrentValues.length === 1
      ? {
          species: speciesOfCurrentValues[0].species ?? placeholderSymbol,
          tangibleSpecies: speciesOfCurrentValues[0].tangibleSpecies ?? [],
        }
      : INDETERMINATE
  ) satisfies
    | IndeterminateValue
    | {
        species: LumberSpecies | PlaceholderSymbol;
        tangibleSpecies: readonly LumberSpecies[];
      };

  const gradesOfCurrentValues = uniqWith(values, gradeMatches);
  const commonGrade = (
    gradesOfCurrentValues.length === 1
      ? {
          grade: gradesOfCurrentValues[0].grade ?? placeholderSymbol,
          tangibleGrades: gradesOfCurrentValues[0].tangibleGrades ?? [],
        }
      : INDETERMINATE
  ) satisfies
    | IndeterminateValue
    | {
        grade: LumberGrade | PlaceholderSymbol;
        tangibleGrades: readonly LumberGrade[];
      };

  const treatmentsOfCurrentValues = uniq(values.map((value) => value.treatmentType));
  const commonTreatment = (
    treatmentsOfCurrentValues.length === 1 ? (treatmentsOfCurrentValues[0] ?? placeholderSymbol) : INDETERMINATE
  ) satisfies LumberTreatmentType | IndeterminateValue | PlaceholderSymbol;

  return (
    <>
      <PropertySelectField
        sx={props.inputSx}
        property="Dimensions"
        options={dimensionOptions}
        value={commonDimension}
        getName={getLumberDimensionString}
        getValue={getLumberDimensionString}
        submit={(lumber) => {
          props.onChange({
            patch: {
              nominalThickness: lumber.nominalThickness,
              nominalWidth: lumber.nominalWidth,

              // In strict mode we always return an entire valid lumber, so these fields must be set.
              actualThickness: props.strict ? (lumber as Lumber).actualThickness : undefined,
              actualWidth: props.strict ? (lumber as Lumber).actualWidth : undefined,
            },
            level: LumberPropertyLevel.Thickness,
          });
        }}
        disabled={props.disabled}
      />

      {values.length === 1 && props.showAdvanced && (
        <MaterialPropertyValuePair
          sx={props.inputSx}
          property="Actual Dimensions"
          value={`${values[0].actualThickness}" x ${values[0].actualWidth}"`}
        />
      )}

      <PropertySelectField
        sx={props.inputSx}
        property="Species"
        options={speciesOptions}
        getName={(value) =>
          value.species === placeholderSymbol
            ? startCase((props as LooseModeProps).placeholder)
            : formatLumberSpeciesLong(value.species, value.tangibleSpecies)
        }
        getValue={(value) =>
          value.species === placeholderSymbol
            ? startCase((props as LooseModeProps).placeholder)
            : formatLumberSpeciesLong(value.species, value.tangibleSpecies)
        }
        value={commonSpecies}
        submit={(value) => {
          props.onChange({
            patch: {
              species: value.species === placeholderSymbol ? undefined : value.species,
              tangibleSpecies: value.species === placeholderSymbol ? undefined : value.tangibleSpecies,
            },
            level: LumberPropertyLevel.Species,
          });
        }}
        disabled={props.disabled}
      />

      <PropertySelectField
        sx={props.inputSx}
        property="Grade"
        options={gradeOptions}
        getName={(value) =>
          value.grade === placeholderSymbol
            ? (props as LooseModeProps).placeholder
            : formatLumberGrade(value.grade, value.tangibleGrades)
        }
        getValue={(value) =>
          value.grade === placeholderSymbol
            ? (props as LooseModeProps).placeholder
            : formatLumberGrade(value.grade, value.tangibleGrades)
        }
        value={commonGrade}
        submit={(value) => {
          props.onChange({
            patch: {
              grade: value.grade === placeholderSymbol ? undefined : value.grade,
              tangibleGrades: value.grade === placeholderSymbol ? undefined : value.tangibleGrades,
            },
            level: LumberPropertyLevel.Grade,
          });
        }}
        disabled={props.disabled}
      />

      <PropertySelectField
        sx={props.inputSx}
        property="Treatment Type"
        options={treatmentOptions}
        getName={(value) => startCase(value === placeholderSymbol ? (props as LooseModeProps).placeholder : value)}
        getValue={renderSelectValue}
        value={commonTreatment ?? placeholderSymbol}
        submit={(treatmentType) => {
          props.onChange({
            patch: { treatmentType: treatmentType === placeholderSymbol ? undefined : treatmentType },
            level: LumberPropertyLevel.Treatment,
          });
        }}
        disabled={props.disabled}
      />
    </>
  );
}
